import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  AgentEntitiesResponse,
  AgentOnboardClientPayload,
  CheckRegNoResponse,
  CompanyNameLookup,
  Context,
  ContextsResponse,
  CreateEntityPayload,
  DeletedEntitiesResponse,
  Entity,
  EntityDetail,
} from './entity.types';

@Injectable({ providedIn: 'root' })
export class EntityService {
  private readonly http = inject(HttpClient);

  /**
   * Cached contexts for the signed-in user, shared with reactive consumers
   * (e.g. the header's role-aware nav). `null` means "not yet loaded".
   * Refreshed via {@link loadContexts} and cleared on logout via
   * {@link clearContexts}.
   */
  private readonly contextsState = signal<Context[] | null>(null);
  readonly contexts = this.contextsState.asReadonly();

  /**
   * True only for platform administrators — a system context whose role is
   * `capx-admin`. Mirrors the backend `is_platform_admin` and `adminGuard` exactly.
   *
   * NOTE: do NOT proxy this off `ops_tier == null`. `ops_tier` lives on the *user*,
   * so the backend stamps it on EVERY one of that user's system contexts — including
   * the `capx-admin` one. A dual-hat user (platform admin who also sits on Ops, e.g.
   * the seeded `admin@capx.local`) therefore has a `capx-admin` context with a
   * non-null `ops_tier`; keying on the role is the only correct signal.
   */
  readonly isAdmin = computed(() =>
    (this.contextsState() ?? []).some((c) => c.type === 'system' && c.role === 'capx-admin'),
  );

  /** True when the user has an ops tier on a system context — mirrors `opsGuard`. */
  readonly isOps = computed(() =>
    (this.contextsState() ?? []).some((c) => c.type === 'system' && c.ops_tier != null),
  );

  /** True when the user has an agent context — drives the Agent nav group. */
  readonly isAgent = computed(() => (this.contextsState() ?? []).some((c) => c.type === 'agent'));

  /**
   * True only when the user is the ADMIN of their agency — the sole role that may
   * manage the agency team (backend and the team page both bounce non-admins).
   * Individual agents (no agency) are never admins, so their Team link stays hidden.
   */
  readonly isAgencyAdmin = computed(() =>
    (this.contextsState() ?? []).some(
      (c) => c.type === 'agent' && c.agency?.member_role === 'ADMIN',
    ),
  );

  getContexts(): Observable<ContextsResponse> {
    return this.http
      .get<ContextsResponse>(`${environment.apiUrl}/api/me/contexts/`)
      .pipe(tap((res) => this.contextsState.set(res.contexts)));
  }

  /** Fetch and cache contexts; safe to call repeatedly. */
  loadContexts(): void {
    this.getContexts().subscribe({ error: () => this.contextsState.set([]) });
  }

  /** Drop cached contexts (call on logout). */
  clearContexts(): void {
    this.contextsState.set(null);
  }

  checkRegNo(regNo: string): Observable<CheckRegNoResponse> {
    return this.http.post<CheckRegNoResponse>(`${environment.apiUrl}/api/entities/check-reg-no/`, {
      reg_no: regNo,
    });
  }

  companyLookup(regNo: string): Observable<CompanyNameLookup> {
    return this.http.post<CompanyNameLookup>(`${environment.apiUrl}/api/entities/company-lookup/`, {
      reg_no: regNo,
    });
  }

  /** The name still to be confirmed for this business (DOC-93 §1), or null. */
  getNameCheck(shortId: string): Observable<{ registered_name: string | null }> {
    return this.http.get<{ registered_name: string | null }>(
      `${environment.apiUrl}/api/entities/${shortId}/name-check/`,
    );
  }

  answerNameCheck(
    shortId: string,
    answer: 'YES' | 'NO',
  ): Observable<{ registered_name: string | null }> {
    return this.http.post<{ registered_name: string | null }>(
      `${environment.apiUrl}/api/entities/${shortId}/name-check/`,
      { answer },
    );
  }

  createEntity(payload: CreateEntityPayload): Observable<Entity> {
    return this.http.post<Entity>(`${environment.apiUrl}/api/entities/`, payload);
  }

  getEntity(id: string): Observable<EntityDetail> {
    return this.http.get<EntityDetail>(`${environment.apiUrl}/api/entities/${id}/`);
  }

  /**
   * Re-elect into Debtors Finance (Constitution §A2.3) — the client's own act,
   * no Ops step: "a deferral the client chose should be reversible by the
   * client". Records the intent on the business; it opens no application.
   */
  reElectDebtorsFinance(id: string): Observable<EntityDetail> {
    return this.http.post<EntityDetail>(
      `${environment.apiUrl}/api/entities/${id}/df-re-election/`,
      {},
    );
  }

  deleteEntity(id: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/api/entities/${id}/`);
  }

  restoreEntity(id: string): Observable<EntityDetail> {
    return this.http.post<EntityDetail>(`${environment.apiUrl}/api/entities/${id}/restore/`, {});
  }

  getDeletedEntities(): Observable<DeletedEntitiesResponse> {
    return this.http.get<DeletedEntitiesResponse>(`${environment.apiUrl}/api/entities/deleted/`);
  }

  getAgentEntities(): Observable<AgentEntitiesResponse> {
    return this.http.get<AgentEntitiesResponse>(
      `${environment.apiUrl}/api/entities/agent/entities/`,
    );
  }

  /** Onboard a new client business as an agent and invite its responsible person. */
  onboardClient(payload: AgentOnboardClientPayload): Observable<Entity> {
    return this.http.post<Entity>(
      `${environment.apiUrl}/api/entities/agent/onboard-client/`,
      payload,
    );
  }
}
